import Color from 'color';
import { equals, range, uniqBy } from 'ramda';
import { uniqueWith } from 'remeda';
import type { Location, NodeEntry, NodeMatch, Point } from 'slate';
import { Editor, Node, Path, Range } from 'slate';

import { isRenderModelTableCellBoundingBox } from './bounding-boxes';
import { applySuggestions, mergeSuggestedEdits } from './suggested-edits';
import type { Length } from '../../types';
import {
  arrayEntries,
  convertDynamicLength,
  convertLength,
  convertUnit,
  filterBy,
  mapObjectValues,
  maxValueBy,
  mergeWithDefaults,
  sumBy,
} from '../../util';
import type {
  BaseRenderModelTable,
  BaseRenderModelTableCellProps,
  BaseRenderModelTableRow,
  PartialTableBorders,
  RenderedTableBorder,
  RenderedTableCellBorders,
  RenderModelTableCell,
  RenderModelTableRow,
  Shade,
  TableAlign,
  TableBorder,
  TableBorders,
  TableCellElement,
  TableCellMargins,
  TableCellProps,
  TableElement,
  TableGridRect,
  TableProps,
  TableRowElement,
  TableRowProps,
  TableWidth,
} from '../internal';
import {
  DEFAULT_COLUMN_WIDTH_TWIPS,
  defaultBorder,
  defaultTableBorders,
  defaultTableCellMargins,
  isRenderModelTableCell,
  isTableCellElement,
  isTableCellEntry,
  isTableCellFormatEdit,
  isTableElement,
  isTableFormatEdit,
  isTableRowElement,
  Queries,
  tableBorderStyleWeights,
} from '../internal';

/** A cell of a table row with the grid columns it occupies. */
export interface TableRowCellRange {
  cellIndex: number;
  endColumnIndex: number;
  startColumnIndex: number;
  tableCell: TableCellElement;
}

export const tableQueries = {
  aboveTableCell(
    editor: Editor,
    options: { at?: Point } = {}
  ): NodeEntry<TableCellElement> | undefined {
    const { at = editor?.selection?.focus } = options;

    if (!at) {
      console.warn(`The at parameter is ${at}. Pass a valid Point.`);
      return;
    }

    return Editor.above(editor, {
      at,
      match: isTableCellElement,
    });
  },

  convertTableMargin(margin: number | TableWidth, unit: Length.Unit): number | undefined {
    return convertDynamicLength(margin, unit, {
      convert: () => undefined,
    });
  },

  /** A span of one is the default, so it is stored as an absent prop. */
  getColumnSpanProp(columnSpan: number): number | undefined {
    return columnSpan > 1 ? columnSpan : undefined;
  },

  /**
   * Get the largest group of selected table cells within a single row. If two or more rows are tied
   * for the most selected table cells, only the cells from the first such row are returned. If the
   * selection overflows outside of a single table, an an empty array will be returned.
   */
  getContiguousTableCells(
    editor: Editor,
    options: { at?: Location } = {}
  ): NodeEntry<TableCellElement>[] {
    const { at = editor.selection } = options;
    if (!at) return [];

    /**
     * With an active grid (table cell rectangle) selection, the selected cells are those of the
     * rectangle: return the row with the most cells overlapping the rect's columns.
     */
    const gridSelectionInfo = Queries.getGridSelectionInfoForLocation(editor, { at });
    if (gridSelectionInfo) {
      const { rect, tableEntry } = gridSelectionInfo;

      let largestCellGroup: NodeEntry<TableCellElement>[] = [];
      for (let rowIndex = rect.startRowIndex; rowIndex <= rect.endRowIndex; rowIndex++) {
        const rowCellEntries = Queries.getTableCellEntriesInRowByColumnRange(tableEntry, rowIndex, {
          endColumnIndex: rect.endColumnIndex,
          startColumnIndex: rect.startColumnIndex,
        });
        if (rowCellEntries.length > largestCellGroup.length) largestCellGroup = rowCellEntries;
      }

      return largestCellGroup;
    }

    const atRange = Editor.range(editor, at);

    const tableAboveEntry = Editor.above(editor, { at, match: isTableElement });
    if (!tableAboveEntry) return [];

    const [table, tablePath] = tableAboveEntry;

    return table.children.reduce(
      (largestCellGroup: NodeEntry<TableCellElement>[], _row, rowIndex) => {
        const rowPath = [...tablePath, rowIndex];
        const rowRange = Editor.range(editor, rowPath);
        const rowIntersection = Range.intersection(atRange, rowRange);
        if (rowIntersection) {
          const cellEntries = [
            ...filterBy(
              Editor.nodes(editor, { at: rowIntersection, match: isTableCellElement }),
              ([, cellPath]) => Path.isChild(cellPath, rowPath)
            ),
          ];
          if (cellEntries.length > largestCellGroup.length) return cellEntries;
        }

        return largestCellGroup;
      },
      []
    );
  },

  /**
   * Get the set of selected table rows, including partially selected rows. If the selection
   * overflows outside of a single table, an an empty array will be returned.
   */
  getContiguousTableRows(
    editor: Editor,
    options: { at?: Location } = {}
  ): {
    rowEntries: NodeEntry<TableRowElement>[];
    tableEntry: NodeEntry<TableElement> | null;
  } {
    const { at = editor.selection } = options;
    if (!at) return { rowEntries: [], tableEntry: null };

    const tableAboveEntry = Editor.above(editor, { at, match: isTableElement });
    if (!tableAboveEntry) return { rowEntries: [], tableEntry: null };

    const [, tablePath] = tableAboveEntry;
    const rowEntries = [
      ...filterBy(Editor.nodes(editor, { at, match: isTableRowElement }), ([, rowPath]) =>
        Path.isChild(rowPath, tablePath)
      ),
    ];

    return { rowEntries, tableEntry: tableAboveEntry };
  },

  /**
   * Check if a potential new table cell width is large enough to contain the cell's margins and
   * borders.
   */
  getIsRMTableCellWidthValid(
    table: TableProps,
    tableCell: RenderModelTableCell,
    options: { widthPx: number }
  ): boolean {
    const { widthPx } = options;
    const leftMarginPx = Queries.renderedTableCellMargin(table, tableCell, 'left', 'px');
    const rightMarginPx = Queries.renderedTableCellMargin(table, tableCell, 'right', 'px');
    return widthPx > leftMarginPx + rightMarginPx;
  },

  /**
   * Returns a boolean determining if the editor selection is within a table
   */
  getIsSelectionWithinTable(editor: Editor) {
    const tableAbove = Editor.above(editor, { match: isTableElement });
    const isSelectionWithinTable = !!tableAbove;
    const isNestedTable =
      isSelectionWithinTable &&
      !!Editor.above(editor, { at: tableAbove[1], match: isTableElement });
    return { isNestedTable, isSelectionWithinTable };
  },

  /**
   * Whether every row fills the table's columns. Deleting a row or column that held a merged cell
   * can leave a row short, which renders as a hole in the grid until the table is squared off again.
   */
  getIsTableRectangular(table: TableElement): boolean {
    const columnCount = Queries.getTableColumnCount(table);

    return table.children.every(
      tableRow => Queries.getTableRowColumnCount(tableRow) === columnCount
    );
  },

  getPreviousTableCellInColumn(
    editor: Editor,
    tableCellEntry: NodeEntry<TableCellElement>
  ): NodeEntry<TableCellElement> | null {
    const [, path] = tableCellEntry;
    const targetStartColumnIndex = Queries.getTableCellStartColumn(editor, tableCellEntry);

    const tableRowPath = Path.parent(path);
    if (!Path.hasPrevious(tableRowPath)) return null;

    const previousTableRowPath = Path.previous(tableRowPath);
    let startColumnIndex = 0;

    for (const previousRowTableCellElementEntry of Node.children(editor, previousTableRowPath)) {
      if (!isTableCellEntry(previousRowTableCellElementEntry)) continue;
      if (startColumnIndex === targetStartColumnIndex) return previousRowTableCellElementEntry;

      const [previousRowTableCell] = previousRowTableCellElementEntry;
      startColumnIndex += Queries.renderedTableCellColumnSpan(previousRowTableCell);
    }

    return null;
  },

  /**
   * For a given table row, find the table cell containing or closest to the given absolute
   * horizontal position. This is used only to handle vertical selection changes, not for resolving
   * clicks.
   */
  getRMTableCellAtAbsoluteXPosition(
    editor: Editor,
    options: { rMTableRowEntry: NodeEntry<RenderModelTableRow>; xPosition: number }
  ): NodeEntry<RenderModelTableCell> | null {
    const { rMTableRowEntry, xPosition } = options;

    const { xLeft: rowLeft } = Queries.getRMNodeAbsolutePosition(editor, rMTableRowEntry);

    const cellBoundingBoxes = Queries.getRMBoundingBoxes(editor, rMTableRowEntry).filter(
      isRenderModelTableCellBoundingBox
    );

    for (const { leftPx, rMNodeEntry, widthPx } of cellBoundingBoxes) {
      const cellLeft = rowLeft + leftPx;
      const cellRight = cellLeft + widthPx;
      if (xPosition < cellRight) return rMNodeEntry;
    }

    return cellBoundingBoxes.at(-1)?.rMNodeEntry ?? null;
  },

  /**
   * Get the width available for the content of a table cell.
   */
  getRMTableCellContentWidthPx(
    table: TableProps,
    tableCell: BaseRenderModelTableCellProps
  ): number {
    const leftMarginPx = Queries.renderedTableCellMargin(table, tableCell, 'left', 'px');
    const rightMarginPx = Queries.renderedTableCellMargin(table, tableCell, 'right', 'px');
    return tableCell.widthPx - leftMarginPx - rightMarginPx;
  },

  /**
   * Get the maximum border width of a given table cell in a given direction. Note that the effect
   * of borders on cell content is already included in renderedTableCellMargins.
   */
  getRMTableCellMaximumBorderWidth(
    tableCell: BaseRenderModelTableCellProps,
    side: keyof RenderedTableCellBorders,
    unit: Length.Unit
  ) {
    if (side === 'top' || side === 'bottom') {
      return maxValueBy(
        tableCell.renderedBorders[side],
        border => Queries.renderedTableBorderWidth(border, unit),
        0
      );
    }

    return Queries.renderedTableBorderWidth(tableCell.renderedBorders[side], unit);
  },

  /**
   * Get the maximum border width of a given table row in a given direction.
   */
  getRMTableRowMaximumBorderWidth(
    tableRow: BaseRenderModelTableRow,
    side: 'top' | 'bottom',
    unit: Length.Unit
  ) {
    return maxValueBy(
      tableRow.children,
      tableCell => Queries.getRMTableCellMaximumBorderWidth(tableCell, side, unit),
      0
    );
  },

  getSelectedTableCellBorder(editor: Editor): {
    color: TableBorder['color'] | null;
    size: TableBorder['size'] | null;
    style: TableBorder['style'] | null;
  } {
    const nullResult = { color: null, size: null, style: null } as const;

    const { selection } = editor;
    if (!selection) return nullResult;

    const tableCellEntries =
      Queries.getGridSelectionCellEntries(editor) ??
      Array.from(Queries.getTableCellEntriesForLocation(editor, { at: selection }));
    if (tableCellEntries.length === 0) return nullResult;

    const allBorders: TableBorder[] = tableCellEntries.flatMap(([, cellPath]) => {
      const [rMNode] = Queries.renderModelNodeFromCMLocation(editor, cellPath);
      if (!isRenderModelTableCell(rMNode)) return [];
      const {
        renderedBorders: { bottom, left, right, top },
      } = rMNode;
      return [left, right, ...bottom, ...top];
    });

    const firstBorder = allBorders.at(0);
    if (!firstBorder) return nullResult;

    return {
      color: allBorders.every(b => b.color === firstBorder.color) ? firstBorder.color : null,
      size: allBorders.every(b => b.size === firstBorder.size) ? firstBorder.size : null,
      style: allBorders.every(b => b.style === firstBorder.style) ? firstBorder.style : null,
    };
  },

  /**
   * The cell fill (shade) shared by every selected cell, or null when they differ or none is
   * selected.
   */
  getSelectedTableCellShade(editor: Editor): Shade | null {
    const { selection } = editor;
    if (!selection) return null;

    const tableCellEntries =
      Queries.getGridSelectionCellEntries(editor) ??
      Array.from(Queries.getTableCellEntriesForLocation(editor, { at: selection }));
    if (tableCellEntries.length === 0) return null;

    const shades = tableCellEntries.map(([cell]) => Queries.renderedTableCellShade(cell) ?? null);

    const [firstShade] = shades;
    return shades.every(shade => equals(shade, firstShade)) ? firstShade : null;
  },

  /**
   * Get suggested table cell props (shade, margins) that are stored in suggestedEdits.
   */
  getSuggestedTableCellProps(tableCell: Omit<TableCellElement, 'children'>) {
    const formatEdits = (tableCell.suggestedEdits ?? []).filter(isTableCellFormatEdit);
    return mergeSuggestedEdits(formatEdits);
  },

  /**
   * Get suggested table props (align, borders, indent, shade) that are stored in suggestedEdits.
   */
  getSuggestedTableProps(table: Omit<TableElement, 'children'>) {
    const formatEdits = (table.suggestedEdits ?? []).filter(isTableFormatEdit);
    return mergeSuggestedEdits(formatEdits);
  },

  /**
   * Determines whether one table cell border takes precedence over another using the rules defined
   * in 17.4.66 tcBorders.
   */
  getTableBorderTakesPrecedence(
    border: TableBorder,
    another: TableBorder,
    options: {
      /**
       * Whether the border given as the first argument appears before the other border in reading
       * order.
       */
      isForward: boolean;
    }
  ): boolean {
    // If the styles differ, use the style with the highest weight
    if (border.style !== another.style) {
      const borderWeight = tableBorderStyleWeights[border.style];
      const anotherWeight = tableBorderStyleWeights[another.style];

      // If the weights are equal, use the higher on the list of styles
      if (borderWeight === anotherWeight) {
        const styleKeys = Object.keys(tableBorderStyleWeights);
        const borderIndex = styleKeys.indexOf(border.style);
        const anotherIndex = styleKeys.indexOf(another.style);
        return borderIndex < anotherIndex;
      }

      return borderWeight > anotherWeight;
    }

    let color: Color | null = null;
    let anotherColor: Color | null = null;

    try {
      color = new Color(border.color);
    } catch {
      console.warn('Failed to parse border color: ' + border.color);
    }

    try {
      anotherColor = new Color(another.color);
    } catch {
      console.warn('Failed to parse border color: ' + another.color);
    }

    if (color && anotherColor) {
      // Use the border with the lowest brightness (R + B + 2G)
      const brightness = color.red() + color.blue() + 2 * color.green();
      const anotherBrightness = anotherColor.red() + anotherColor.blue() + 2 * anotherColor.green();
      if (brightness < anotherBrightness) return true;
      if (brightness > anotherBrightness) return false;

      // If the brightnesses are tied, exclude red and try again
      const brightnessWithoutRed = color.blue() + 2 * color.green();
      const anotherBrightnessWithoutRed = anotherColor.blue() + 2 * anotherColor.green();
      if (brightnessWithoutRed < anotherBrightnessWithoutRed) return true;
      if (brightnessWithoutRed > anotherBrightnessWithoutRed) return false;

      // If the brightnesses are tied, compare only green
      if (color.green < anotherColor.green) return true;
      if (color.green > anotherColor.green) return false;
    }

    // As a final tiebreak, use the border belonging to the left or upper cell
    return options.isForward;
  },

  /**
   * Returns the table entry with the given locationId, or undefined if not found.
   */
  getTableByLocationId(editor: Editor, locationId: string): NodeEntry<TableElement> | undefined {
    const [entry] = Editor.nodes<TableElement>(editor, {
      at: [],
      match: node => isTableElement(node) && node.locationId === locationId,
    });

    return entry;
  },

  /**
   * Get the base margin of a table cell (the combination of the table-level margins and any
   * cell-specific overrides).
   */
  getTableCellBaseMargin(
    table: TableProps,
    tableCell: TableCellProps,
    side: keyof TableCellMargins,
    unit: Length.Unit
  ): number {
    const cellLevelMargin = applySuggestions(tableCell).margins?.[side];
    if (cellLevelMargin && validateMargin(cellLevelMargin))
      return convertLength(cellLevelMargin, unit);

    const tableLevelMargin = applySuggestions(table).margins?.[side];
    if (tableLevelMargin && validateMargin(tableLevelMargin))
      return convertLength(tableLevelMargin, unit);

    const defaultMargin = defaultTableCellMargins[side];
    return convertLength(defaultMargin, unit);

    /** Filter out 'auto' and 'pct' margins, which should be ignored */
    function validateMargin(
      margin: TableWidth | number
    ): margin is Extract<Length.Absolute, TableWidth> {
      return typeof margin === 'number' || (margin.type !== 'auto' && margin.type !== 'pct');
    }
  },

  /**
   * Get the index of the column on which the given table cell ends.
   */
  getTableCellEndColumn(editor: Editor, [tableCell, path]: NodeEntry<TableCellElement>): number {
    return Queries.getTableCellEndColumnFromStartColumn(
      tableCell,
      Queries.getTableCellStartColumn(editor, [tableCell, path])
    );
  },

  /**
   * Get the column index on which a table cell ends, given the column on which it starts.
   */
  getTableCellEndColumnFromStartColumn(
    tableCell: TableCellProps,
    startColumnIndex: number
  ): number {
    return startColumnIndex + Queries.renderedTableCellColumnSpan(tableCell) - 1;
  },

  /**
   * Get the set of table cells for a location.
   *
   * If the location is a range, find the table above this range and return all table cells
   * belonging to this table that are included in the range (excluding cells in nested tables).
   *
   * If the location is a path or a point that refers to a table cell or a descendant of a table
   * cell, return the lowest such table cell.
   *
   * If the location is a path that is not a table cell or a descendant of a table cell (such as the
   * path of a top-level table or table row), return the set of lowest table cells contained within
   * that path. (Note that relying on this case may produce unexpected results in some cases. For
   * example, passing the path of a top-level table that contains one or more nested tables will
   * return a mixture of cells from the parent table and the descendant tables due to the mode being
   * lowest. In addition, passing the path of a nested table will behave completely differently from
   * passing the path of a top-level table.)
   */
  *getTableCellEntriesForLocation(
    editor: Editor,
    options: { at: Location; match?: NodeMatch<TableCellElement> }
  ): IterableIterator<NodeEntry<TableCellElement>> {
    const { at, match = isTableCellElement } = options;

    if (Range.isRange(at)) {
      const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement });
      if (!tableEntry) return;
      const [, tablePath] = tableEntry;

      yield* Editor.nodes(editor, {
        at,
        match: (node, path) =>
          match(node, path) &&
          Path.isAncestor(tablePath, path) &&
          path.length === tablePath.length + 2,
        mode: 'lowest',
      });
      return;
    }

    const aboveEntry = Queries.atOrAbove(editor, { at, match });
    if (aboveEntry && isTableCellEntry(aboveEntry)) {
      yield aboveEntry;
      return;
    }

    yield* Editor.nodes(editor, { at, match, mode: 'lowest' });
  },

  /**
   * Get the set of table cells that intersect with a given table column.
   */
  getTableCellEntriesInColumn(
    tableEntry: NodeEntry<TableElement>,
    columnIndex: number
  ): {
    endColumnIndex: number;
    startColumnIndex: number;
    tableCellEntry: NodeEntry<TableCellElement>;
  }[] {
    const [table, tablePath] = tableEntry;

    return table.children.flatMap((tableRow, tableRowIndex) => {
      const range = Queries.getTableRowCellRanges(tableRow).find(
        ({ endColumnIndex, startColumnIndex }) =>
          columnIndex >= startColumnIndex && columnIndex <= endColumnIndex
      );
      if (!range) return [];

      const { cellIndex, endColumnIndex, startColumnIndex, tableCell } = range;
      const tableCellPath = [...tablePath, tableRowIndex, cellIndex];

      return [{ endColumnIndex, startColumnIndex, tableCellEntry: [tableCell, tableCellPath] }];
    });
  },

  /**
   * Returns table cell entries in the given row whose column span overlaps the specified column range.
   * Used to find cells that share a horizontal border (e.g. top/bottom neighbors of horizontally merged cells).
   */
  getTableCellEntriesInRowByColumnRange(
    tableEntry: NodeEntry<TableElement>,
    rowIndex: number,
    options: { endColumnIndex: number; startColumnIndex: number }
  ): NodeEntry<TableCellElement>[] {
    const [table, tablePath] = tableEntry;
    const { endColumnIndex: targetEndColumnIndex, startColumnIndex: targetStartColumnIndex } =
      options;

    const row = table.children[rowIndex];
    if (!row) return [];

    return Queries.getTableRowCellRanges(row)
      .filter(
        ({ endColumnIndex, startColumnIndex }) =>
          endColumnIndex >= targetStartColumnIndex && startColumnIndex <= targetEndColumnIndex
      )
      .map(({ cellIndex, tableCell }) => [tableCell, [...tablePath, rowIndex, cellIndex]]);
  },

  /**
   * Get the index of the column on which the given table cell starts.
   */
  getTableCellStartColumn(editor: Editor, [, path]: NodeEntry<TableCellElement>): number {
    const [tableRow] = Editor.parent(editor, path);
    if (!isTableRowElement(tableRow)) throw new Error('Expected table row');
    const tableCellIndex = path.at(-1) ?? 0;
    const previousCellsInRow = tableRow.children.slice(0, tableCellIndex);
    return sumBy(previousCellsInRow, Queries.renderedTableCellColumnSpan);
  },

  /**
   * For a given table row, get the cell for each column index in a range. The returned array is
   * guaranteed to equal the length of the given column range. If a horizontally merged cell occurs
   * in this range, it will appear multiple times. If no cell exists for a column, null is returned instead.
   */
  getTableCellsInColumnRange(
    tableRow: TableRowElement | null,
    options: { endColumnIndex: number; startColumnIndex: number }
  ): (TableCellElement | null)[] {
    const { endColumnIndex: targetEndColumnIndex, startColumnIndex: targetStartColumnIndex } =
      options;

    const result: (TableCellElement | null)[] = new Array(
      targetEndColumnIndex - targetStartColumnIndex + 1
    ).fill(null);

    if (!tableRow) return result;

    for (const { endColumnIndex, startColumnIndex, tableCell } of Queries.getTableRowCellRanges(
      tableRow
    )) {
      const firstColumnIndex = Math.max(startColumnIndex, targetStartColumnIndex);
      const lastColumnIndex = Math.min(endColumnIndex, targetEndColumnIndex);

      for (let columnIndex = firstColumnIndex; columnIndex <= lastColumnIndex; columnIndex++)
        result[columnIndex - targetStartColumnIndex] = tableCell;
    }

    return result;
  },

  /**
   * Get the set of table columns intersecting the selection, and then the set of cells in those
   * columns. Each cell is guaranteed to appear at most once, and all cells are in ascending order
   * of path.
   */
  getTableCellsInSelectedColumns(
    editor: Editor,
    options: {
      at?: Location;
    } = {}
  ): {
    endColumnIndex: number;
    startColumnIndex: number;
    tableCellEntries: NodeEntry<TableCellElement>[];
  } | null {
    const { at = editor.selection } = options;
    if (!at) return null;

    /**
     * With an active grid (table cell rectangle) selection, the selected columns are exactly the
     * rect's columns. Deriving them from the selected cells would widen the range whenever a merged
     * cell sticks out of the rectangle.
     */
    const gridSelectionInfo = Queries.getGridSelectionInfoForLocation(editor, { at });

    const tableEntry =
      gridSelectionInfo?.tableEntry ?? Editor.above(editor, { at, match: isTableElement });
    if (!tableEntry) return null;

    const columnRange = gridSelectionInfo?.rect ?? getContiguousCellColumnRange(editor, at);
    if (!columnRange) return null;
    const { endColumnIndex, startColumnIndex } = columnRange;

    const columnIndices = range(startColumnIndex, endColumnIndex + 1);

    const tableCellEntries = columnIndices
      .flatMap(columnIndex => Queries.getTableCellEntriesInColumn(tableEntry, columnIndex))
      .map(({ tableCellEntry }) => tableCellEntry);

    const uniqueTableCellEntries = uniqueWith(tableCellEntries, ([, pathA], [, pathB]) =>
      Path.equals(pathA, pathB)
    );

    const sortedTableCellEntries = uniqueTableCellEntries.toSorted(([, pathA], [, pathB]) =>
      Path.compare(pathA, pathB)
    );

    return { endColumnIndex, startColumnIndex, tableCellEntries: sortedTableCellEntries };
  },

  getTableColumnCount(table: TableElement | BaseRenderModelTable): number {
    return maxValueBy(table.children, Queries.getTableRowColumnCount, 0);
  },

  /**
   * Get the maximum border width of a given table in a given direction.
   */
  getTableMaximumBorderWidth(
    table: TableElement | BaseRenderModelTable,
    side: keyof RenderedTableCellBorders,
    unit: Length.Unit
  ): number {
    const tableBorders = getTableBordersWithSuggestions(table);
    const columnCount = Queries.getTableColumnCount(table);

    /**
     * Since the only borders affecting the table's maximum border width in any given direction
     * must be on the outside of the table, border conflicts cannot occur. This lets us get the
     * width of any relevant border without relying on the render model.
     */
    function getTableCellBorderWidth(tableCell: TableCellProps) {
      const tableCellBorders = getTableBordersWithSuggestions(tableCell);
      const border = Queries.mergeTableBorderWithDefault(
        tableCellBorders[side] ?? tableBorders[side]
      );
      return Queries.renderedTableBorderWidth(border, unit);
    }

    if (side === 'top' || side === 'bottom') {
      const tableRow = table.children.at(side === 'top' ? 0 : -1);
      if (!tableRow) return 0;
      return maxValueBy(tableRow.children, getTableCellBorderWidth);
    }

    // For the right side, we only care about rows that aren't missing any columns
    const fullRows = table.children.filter(
      tableRow => Queries.getTableRowColumnCount(tableRow) === columnCount
    );
    const eligibleRows = side === 'right' ? fullRows : table.children;

    return maxValueBy(
      eligibleRows,
      tableRow => {
        const tableCell = tableRow.children.at(side === 'left' ? 0 : -1);
        if (!tableCell) return 0;
        return getTableCellBorderWidth(tableCell);
      },
      0
    );
  },

  getTableMinimumColumnWidths(table: TableElement): number[] {
    const columnCount = Queries.getTableColumnCount(table);
    const minimumColumnWidths: number[] = new Array(columnCount).fill(0);

    for (const tableRow of table.children) {
      for (const { startColumnIndex, tableCell } of Queries.getTableRowCellRanges(tableRow)) {
        const marginLeft = Queries.getTableCellBaseMargin(table, tableCell, 'left', 'tw');
        const marginRight = Queries.getTableCellBaseMargin(table, tableCell, 'right', 'tw');
        const minimumWidth = Math.max(35, marginLeft + marginRight + 20);

        // If the cell spans multiple columns, divide the minimum width evenly between them
        const columnSpan = Queries.renderedTableCellColumnSpan(tableCell);
        const minimumWidths = scaleProportionallyAsIntegers(
          new Array(columnSpan).fill(1),
          minimumWidth
        );

        for (const [relativeColumnIndex, min] of arrayEntries(minimumWidths))
          growColumn(minimumColumnWidths, startColumnIndex + relativeColumnIndex, min);
      }
    }

    return minimumColumnWidths;
  },

  /**
   * Returns the table-row entry with the given locationId, or undefined if not found.
   */
  getTableRowByLocationId(
    editor: Editor,
    locationId: string
  ): NodeEntry<TableRowElement> | undefined {
    const [entry] = Editor.nodes<TableRowElement>(editor, {
      at: [],
      match: node => isTableRowElement(node) && node.locationId === locationId,
    });

    return entry;
  },

  /** Each cell of a table row with the grid columns it occupies, in row order. */
  getTableRowCellRanges(tableRow: TableRowElement): TableRowCellRange[] {
    const ranges: TableRowCellRange[] = [];
    let startColumnIndex = 0;

    for (const [cellIndex, tableCell] of arrayEntries(tableRow.children)) {
      const endColumnIndex = Queries.getTableCellEndColumnFromStartColumn(
        tableCell,
        startColumnIndex
      );
      ranges.push({ cellIndex, endColumnIndex, startColumnIndex, tableCell });
      startColumnIndex = endColumnIndex + 1;
    }

    return ranges;
  },

  getTableRowColumnCount(tableRow: TableRowElement | BaseRenderModelTableRow): number {
    return sumBy(tableRow.children, Queries.renderedTableCellColumnSpan);
  },

  getTableRowSpecifiedHeight(tableRow: TableRowProps, unit: Length.Unit): number | undefined {
    const { height } = applySuggestions(tableRow);
    if (height === undefined) return undefined;
    return convertLength(height, unit);
  },

  /**
   * Returns all content model table cells in the vertical merge group containing the given cell,
   * ordered from first (top) to last (bottom). For non-merged cells, returns a single-element array.
   */
  getVerticallyMergedTableCellGroup(
    editor: Editor,
    tableCellEntry: NodeEntry<TableCellElement>
  ): NodeEntry<TableCellElement>[] {
    const startEntry = Queries.getVerticallyMergedTableCellStart(editor, tableCellEntry);
    const group: NodeEntry<TableCellElement>[] = [startEntry];

    // When verticalMerge is undefined, then this cell is not part of vertically merged group
    if (Queries.renderedTableCellVerticalMerge(startEntry[0]) === undefined) return group;

    const tableEntry = Editor.above(editor, {
      at: startEntry[1],
      match: isTableElement,
    });
    if (!tableEntry) return group;

    const [table, tablePath] = tableEntry;
    const [, startPath] = startEntry;
    const startColumnIndex = Queries.getTableCellStartColumn(editor, startEntry);
    let currentRowIndex = startPath[tablePath.length];

    while (true) {
      const nextRowIndex = currentRowIndex + 1;
      const nextRow = table.children[nextRowIndex];
      if (!nextRow) break;

      const nextRange = Queries.getTableRowCellRanges(nextRow).find(
        range => range.startColumnIndex === startColumnIndex
      );
      if (!nextRange) break;
      if (Queries.renderedTableCellVerticalMerge(nextRange.tableCell) !== 'continue') break;

      group.push([nextRange.tableCell, [...tablePath, nextRowIndex, nextRange.cellIndex]]);
      currentRowIndex = nextRowIndex;
    }
    return group;
  },

  /**
   * Get the first content model table cell of a vertically merged cell.
   */
  getVerticallyMergedTableCellStart(
    editor: Editor,
    tableCellEntry: NodeEntry<TableCellElement>
  ): NodeEntry<TableCellElement> {
    const [tableCell] = tableCellEntry;

    const verticalMerge = Queries.renderedTableCellVerticalMerge(tableCell);
    if (verticalMerge !== 'continue') return tableCellEntry;

    const previousTableCellEntry = Queries.getPreviousTableCellInColumn(editor, tableCellEntry);
    if (!previousTableCellEntry) return tableCellEntry;

    const [previousTableCell] = previousTableCellEntry;
    const previousVerticalMerge = Queries.renderedTableCellVerticalMerge(previousTableCell);

    return previousVerticalMerge === undefined
      ? tableCellEntry
      : Queries.getVerticallyMergedTableCellStart(editor, previousTableCellEntry);
  },

  mergePartialTableBordersWithDefaults(
    tableOrCell: TableProps | TableCellProps
  ): PartialTableBorders {
    const borders = getTableBordersWithSuggestions(tableOrCell);
    return mapObjectValues(borders, Queries.mergeTableBorderWithDefault);
  },

  mergeTableBorderWithDefault(partialBorder: Partial<TableBorder> | undefined): TableBorder {
    return partialBorder ? mergeWithDefaults(partialBorder, defaultBorder) : defaultBorder;
  },

  mergeTableBordersWithDefaults(table: TableProps): TableBorders {
    const bordersWithDefaults = Queries.mergePartialTableBordersWithDefaults(table);
    return mergeWithDefaults(bordersWithDefaults, defaultTableBorders);
  },

  renderedTableAlign(table: TableProps): TableAlign {
    return applySuggestions(table).align ?? 'left';
  },

  renderedTableBorderWidth(border: TableBorder, unit: Length.Unit): number {
    switch (border.style) {
      case 'hidden':
      case 'none':
        return 0;

      default:
        return convertUnit(border.size, 'pt', unit);
    }
  },

  /**
   * Determine which border style should be drawn for a given edge of a table cell, given the cell
   * adjoining this cell on the given edge. This function should be symmetrical for any pair of
   * table cells, except that the draw property of the return value should be true for exactly one
   * ordering of the pair.
   */
  renderedTableCellBorder(
    table: TableElement,
    tableCell: TableCellElement,
    adjoiningCell: TableCellElement | null,
    side: keyof RenderedTableCellBorders
  ): RenderedTableBorder {
    const isHorizontal = side === 'left' || side === 'right';
    const isForward = side === 'right' || side === 'bottom';

    const reverseDirection = (
      {
        bottom: 'top',
        left: 'right',
        right: 'left',
        top: 'bottom',
      } as const
    )[side];

    const tableBorders = getTableBordersWithSuggestions(table);
    const tableCellBorders = getTableBordersWithSuggestions(tableCell);
    const adjoiningCellBorders = adjoiningCell ? getTableBordersWithSuggestions(adjoiningCell) : {};

    const applicableSide = adjoiningCell ? (isHorizontal ? 'insideV' : 'insideH') : side;
    const applicableTableBorder = Queries.mergeTableBorderWithDefault(tableBorders[applicableSide]);

    const basePreferredCellBorder = tableCellBorders[side] ?? null;
    const preferredCellBorder =
      basePreferredCellBorder && Queries.mergeTableBorderWithDefault(basePreferredCellBorder);

    const basePreferredAdjoiningCellBorder = adjoiningCellBorders[reverseDirection] ?? null;
    const preferredAdjoiningCellBorder =
      basePreferredAdjoiningCellBorder &&
      Queries.mergeTableBorderWithDefault(basePreferredAdjoiningCellBorder);

    // If this cell has a preferred border and the other cell doesn't, draw this cell's border.
    if (preferredCellBorder && !preferredAdjoiningCellBorder)
      return { ...preferredCellBorder, draw: true };

    /**
     * If this cell has no preferred border and the other cell does, use the other cell's border,
     * but do not draw it again.
     */
    if (!preferredCellBorder && preferredAdjoiningCellBorder)
      return { ...preferredAdjoiningCellBorder, draw: false };

    /**
     * If neither cell has a preferred border, use the table border, only drawing it if the border
     * belongs to this cell's right or bottom edge or if there is no adjoining cell.
     */
    if (!preferredCellBorder || !preferredAdjoiningCellBorder)
      return { ...applicableTableBorder, draw: isForward || !adjoiningCell };

    /**
     * If both cells have preferred borders, use the more dominant border, only drawing it if it
     * belongs to this cell.
     */
    return Queries.getTableBorderTakesPrecedence(
      preferredCellBorder,
      preferredAdjoiningCellBorder,
      { isForward }
    )
      ? { ...preferredCellBorder, draw: true }
      : { ...preferredAdjoiningCellBorder, draw: false };
  },

  renderedTableCellColumnSpan(tableCell: TableCellProps): number {
    return applySuggestions(tableCell).columnSpan ?? 1;
  },

  /**
   * Get the margins with which the given table cell's content will be rendered with. If half of the
   * border width is greater than the base margin on any given side, then this will be used as the
   * margin for this side.
   */
  renderedTableCellMargin(
    table: TableProps,
    tableCell: BaseRenderModelTableCellProps,
    side: keyof TableCellMargins,
    unit: Length.Unit
  ): number {
    const baseMargin = Queries.getTableCellBaseMargin(table, tableCell, side, unit);
    const borderWidth = Queries.getRMTableCellMaximumBorderWidth(tableCell, side, unit);
    return Math.max(baseMargin, borderWidth / 2);
  },

  renderedTableCellShade(tableCell: TableCellProps) {
    const { shade } = applySuggestions(tableCell);
    /**
     * In previous versions of Aline, a fill of 'auto' could be parsed incorrectly as '#auto'.
     * Handle this case so that existing documents impacted by this are rendered correctly.
     * @see EDI-10 and EDI-271
     */
    if (shade?.fillColor === '#auto') return undefined;
    return shade;
  },

  renderedTableCellVerticalMerge(tableCell: TableCellProps) {
    return applySuggestions(tableCell).verticalMerge;
  },

  /**
   * Determine the actual widths of each column in a table.
   */
  renderedTableColumnWidths(
    table: TableElement,
    options: {
      /** The width available to the table, measured in twips */
      availableWidth: number;
    }
  ): number[] {
    const { availableWidth } = options;
    /**
     * TODO: gridColumns should have type Length.Twip[] | undefined, but due to EDI-240 it can
     * sometimes be null, skipping the default used in destructuring. The `?? []` can be safely
     * removed once this issue is fixed.
     */
    const { gridColumns: unsafeGridColumns = [], width = { type: 'auto' } } =
      applySuggestions(table);
    const gridColumns = unsafeGridColumns ?? [];

    const layoutMode = Queries.renderedTableLayout(table);
    const indent = Queries.renderedTableIndent(table, 'tw');
    const columnCount = Queries.getTableColumnCount(table);
    const preferredColumnWidths = gridColumns.map(width => convertLength(width, 'tw'));

    const preferredTableWidth = convertDynamicLength<number | 'auto'>(width, 'tw', {
      convert: length => {
        if (length.type === 'auto') return 'auto';
        return availableWidth * length.value;
      },
    });

    let columnWidths: number[] = new Array(columnCount).fill(0);

    // Step 1: Define column widths based on preferred column and cell widths
    columnWidths.forEach((_, columnIndex) => {
      const preferredColumnWidth = preferredColumnWidths[columnIndex] ?? DEFAULT_COLUMN_WIDTH_TWIPS;

      for (const tableCellEntry of Queries.getTableCellEntriesInColumn([table, []], columnIndex)) {
        const {
          endColumnIndex,
          startColumnIndex,
          tableCellEntry: [tableCell],
        } = tableCellEntry;

        if (endColumnIndex !== columnIndex) continue;

        const { width = { type: 'auto' } } = applySuggestions(tableCell);

        const preferredCellWidth = convertDynamicLength(width, 'tw', {
          convert: length => {
            if (length.type === 'auto') return preferredColumnWidth;
            return preferredTableWidth === 'auto'
              ? preferredColumnWidth
              : preferredTableWidth * length.value;
          },
        });

        // If a cell spans multiple columns, grow only the last column
        const previousColumnWidths = columnWidths
          .slice(startColumnIndex, endColumnIndex)
          .reduce((sum, width) => sum + width, 0);

        const remainingPreferredWidth = preferredCellWidth - previousColumnWidths;
        growColumn(columnWidths, columnIndex, remainingPreferredWidth);
      }
    });

    // Step 2: Resize columns proportionally to fit inside available width (if applicable)
    if (layoutMode === 'autofit' && preferredTableWidth === 'auto') {
      const totalColumnWidth = columnWidths.reduce((sum, width) => sum + width, 0);

      const maxBorderLeft = Queries.getTableMaximumBorderWidth(table, 'left', 'tw');
      const maxBorderRight = Queries.getTableMaximumBorderWidth(table, 'right', 'tw');

      const availableWidthForColumns =
        availableWidth - indent - Math.ceil(maxBorderLeft / 2 + maxBorderRight / 2);

      if (totalColumnWidth > availableWidthForColumns) {
        columnWidths = scaleProportionallyAsIntegers(columnWidths, availableWidthForColumns);
      }
    }

    // Step 3: Resize columns proportionally to match the preferred table width
    if (preferredTableWidth !== 'auto') {
      columnWidths = scaleProportionallyAsIntegers(columnWidths, preferredTableWidth);
    }

    // Step 4: Grow columns as necessary to fit minimum cell widths
    const minimumColumnWidths = Queries.getTableMinimumColumnWidths(table);
    columnWidths = columnWidths.map((width, index) =>
      Math.max(width, minimumColumnWidths[index] ?? 0)
    );

    return columnWidths;
  },

  renderedTableIndent(table: TableProps, unit: Length.Unit): number {
    const { indent = { type: 'nil' } } = applySuggestions(table);

    return convertDynamicLength(indent, unit, {
      convert: () => 0,
    });
  },

  renderedTableLayout(table: TableProps): TableElement['layout'] {
    return applySuggestions(table).layout ?? 'autofit';
  },

  renderedTableRowCantSplit(tableRow: TableRowProps) {
    return applySuggestions(tableRow).cantSplit;
  },

  /**
   * Return the shade of all table cells in the given table, assuming that all cells have the same
   * shade. Otherwise, return undefined.
   */
  renderedTableShade(table: TableElement): Shade | undefined {
    const allTableCellShades = table.children.flatMap(tableRow =>
      tableRow.children.map(Queries.renderedTableCellShade)
    );

    const uniqueTableCellShades = uniqBy(shade => shade?.fillColor, allTableCellShades);
    return uniqueTableCellShades.length === 1 ? uniqueTableCellShades[0] : undefined;
  },
};

/** The inclusive column range spanned by the contiguous cells at the location, or null when none. */
function getContiguousCellColumnRange(
  editor: Editor,
  at: Location
): Pick<TableGridRect, 'endColumnIndex' | 'startColumnIndex'> | null {
  const contiguousCellEntries = Queries.getContiguousTableCells(editor, { at });

  const firstCellEntry = contiguousCellEntries.at(0);
  const lastCellEntry = contiguousCellEntries.at(-1);
  if (!firstCellEntry || !lastCellEntry) return null;

  return {
    endColumnIndex: Queries.getTableCellEndColumn(editor, lastCellEntry),
    startColumnIndex: Queries.getTableCellStartColumn(editor, firstCellEntry),
  };
}

/**
 * Grow the width of the column at the given index to the given minimum width.
 * It mutates the given columnWidths array.
 */
function growColumn(columnWidths: number[], columnIndex: number, minWidth: number) {
  const oldWidth = columnWidths[columnIndex] ?? 0;
  if (oldWidth < minWidth) {
    columnWidths[columnIndex] = minWidth;
  }
}

/**
 * Scale an array of values so that they sum to a given total. Each resulting value is rounded to an
 * integer while ensuring that the sum is correct.
 */
function scaleProportionallyAsIntegers(values: number[], total: number): number[] {
  if (values.length === 0) return [];
  const originalTotal = values.reduce((sum, x) => sum + x);
  const scaleFactor = total / originalTotal;
  const scaledValues = values.map(x => Math.round(x * scaleFactor));
  const newTotal = scaledValues.reduce((sum, x) => sum + x);
  const error = total - newTotal;
  scaledValues[0] += error;
  return scaledValues;
}

// TODO: Remove this, once https://linear.app/aline/issue/EDI-240/slate-yjs-doesnt-support-storing-null-values-in-node-properties is fixed
function getTableBordersWithSuggestions(tableOrCell: TableProps | TableCellProps) {
  return applySuggestions(tableOrCell).borders ?? {};
}
